import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiGetV2, apiPatchV2 } from "~/shared/api/client";

/**
 * Сущности, у которых суперадмин может править авторство. Значение совпадает с
 * сегментом пути бэкенда: `/v2/attribution/<entity>/{id}`.
 */
export type AttributionEntity = "workspaces" | "projects" | "scenes" | "folders" | "chats";

export interface AttributionCreator {
    userId: string;
    email: string | null;
    name: string | null;
    image: string | null;
}

export interface Attribution {
    creator: AttributionCreator | null;
    createdAt: string | null;
    /** Нижняя граница даты; `null` — сущность не привязана к дате родителя. */
    minCreatedAt: string | null;
    /** Верхняя граница даты; `null` — ограничена только «сейчас». */
    maxCreatedAt: string | null;
    /** `false` — правка запрещена целиком (сегодня это личное пространство). */
    editable: boolean;
    reason: string | null;
}

export interface AttributionUpdate {
    creatorId?: string;
    /** ISO-8601 со смещением — бэкенд сравнивает момент, а не локальное время. */
    createdAt?: string;
}

export const attributionKeys = {
    all: ["attribution"] as const,
    detail: (entity: AttributionEntity, entityId: string) => ["attribution", entity, entityId] as const,
};

export function fetchAttribution(entity: AttributionEntity, entityId: string): Promise<Attribution> {
    return apiGetV2<Attribution>(`/attribution/${entity}/${entityId}`);
}

/**
 * Текущее авторство сущности с границами даты. Отдаёт только суперадмину —
 * поэтому запрос включают лишь после того, как флаг подтверждён.
 */
export function useAttribution(
    entity: AttributionEntity,
    entityId: string | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: attributionKeys.detail(entity, entityId ?? ""),
        queryFn: () => fetchAttribution(entity, entityId!),
        enabled: (options?.enabled ?? true) && !!entityId,
    });
}

/**
 * Смена создателя и/или даты создания. Пустой патч бэкенд отвергает, поэтому
 * вызывающая сторона отправляет только реально изменённые поля.
 */
export function useUpdateAttribution(entity: AttributionEntity, entityId: string | undefined) {
    const qc = useQueryClient();
    return useMutation<Attribution, Error, AttributionUpdate>({
        mutationFn: (payload) => apiPatchV2<Attribution>(`/attribution/${entity}/${entityId}`, payload),
        onSuccess: (updated) => {
            qc.setQueryData(attributionKeys.detail(entity, entityId ?? ""), updated);
        },
    });
}
